import React, { memo, useMemo } from 'react';
import { Droppable, Draggable } from 'react-beautiful-dnd';
import * as Styled from './styled';
import RuleBuilderItem from './rule-builder-item/RuleBuilderItem';
import { RuleBuilderField, RuleBuilderInput } from '.';
import { createReportFieldKey, noop } from '../../../util';
import { RULEBUILDER_DROPPABLE_ID } from './rule-builder-dragdrop-context/RuleBuilderDragDropContext';
import { Icon } from '../../../components';

interface RuleBuilderProps {
  fields: Array<RuleBuilderField>;
  onRemoveField?: (field: RuleBuilderField) => void;
  onAddRule?: (field: RuleBuilderField) => void;
  onCancelFieldEditing?: (field: RuleBuilderField) => void;
  onSaveEditingField?: (field: RuleBuilderField) => void;
  onRemoveRuleClick?: (rule: RuleBuilderInput) => void;
  onEditRuleClick?: (rule: RuleBuilderInput) => void;
  onRuleChange?: (rule: RuleBuilderInput) => void;
}

function RuleBuilder({
  fields,
  onRemoveField,
  onAddRule,
  onCancelFieldEditing,
  onSaveEditingField,
  onRemoveRuleClick,
  onEditRuleClick,
  onRuleChange,
}: RuleBuilderProps): JSX.Element {
  const RenderedItems = useMemo(
    () =>
      fields.map((field, index) => {
        const itemDeleteHandler = (): void => {
          onRemoveField(field);
        };
        const addRuleHandler = (): void => {
          onAddRule(field);
        };
        const onCancelHandler = (): void => {
          onCancelFieldEditing(field);
        };
        const onSaveHandler = (): void => {
          onSaveEditingField(field);
        };

        const fieldKey = createReportFieldKey(field);
        const draggableId = `rulebuilder-${fieldKey}`;
        return (
          <Draggable key={fieldKey} draggableId={draggableId} index={index}>
            {(provided, snapshot): JSX.Element => (
              <Styled.RuleBuilderDragItem
                isDragging={snapshot.isDragging}
                ref={provided.innerRef}
                {...provided.draggableProps}
                {...provided.dragHandleProps}
                style={provided.draggableProps.style}
                data-draggableid={draggableId}
              >
                <Icon className="dragIcon" name="eightDot" />
                <RuleBuilderItem
                  className="ruleBuilderItem"
                  field={field}
                  onDeleteClick={itemDeleteHandler}
                  onAddRuleClick={addRuleHandler}
                  onCancelClick={onCancelHandler}
                  onSaveClick={onSaveHandler}
                  onRemoveRuleClick={onRemoveRuleClick}
                  onEditRuleClick={onEditRuleClick}
                  onRuleChange={onRuleChange}
                />
              </Styled.RuleBuilderDragItem>
            )}
          </Draggable>
        );
      }),
    [
      fields,
      onAddRule,
      onCancelFieldEditing,
      onEditRuleClick,
      onRemoveField,
      onRemoveRuleClick,
      onRuleChange,
      onSaveEditingField,
    ],
  );

  const droppableId = RULEBUILDER_DROPPABLE_ID;
  return (
    <Droppable droppableId={droppableId}>
      {(provided): JSX.Element => (
        <Styled.RuleBuilder
          {...provided.droppableProps}
          ref={provided.innerRef}
          data-droppableid={droppableId}
        >
          {RenderedItems}
          {provided.placeholder}
        </Styled.RuleBuilder>
      )}
    </Droppable>
  );
}

RuleBuilder.defaultProps = {
  onRemoveField: noop,
};

export default memo(RuleBuilder);
